import { ValidationTypes } from "types/validation";
import type { Validation } from "types/validation";

export type ValidationSeverityCounts = {
  validationErrorCount: number;
  validationWarningCount: number;
  validationInfoCount: number;
};

const EMPTY_COUNTS: ValidationSeverityCounts = {
  validationErrorCount: 0,
  validationWarningCount: 0,
  validationInfoCount: 0,
};

/**
 * Count validations by severity. Optionally filter with a predicate
 * (e.g. RULETABLE + matching table id for table summary recounts).
 */
export function countValidationsBySeverity(
  validations: Validation[] | null | undefined,
  predicate?: (validation: Validation) => boolean
): ValidationSeverityCounts {
  const counts: ValidationSeverityCounts = { ...EMPTY_COUNTS };

  for (const v of validations ?? []) {
    if (predicate && !predicate(v)) {
      continue;
    }

    switch (v.type) {
      case ValidationTypes.ERROR:
        counts.validationErrorCount += 1;
        break;
      case ValidationTypes.WARNING:
        counts.validationWarningCount += 1;
        break;
      case ValidationTypes.INFO:
        counts.validationInfoCount += 1;
        break;
      default:
        break;
    }
  }

  return counts;
}

export function totalValidationIssueCount(
  counts: ValidationSeverityCounts
): number {
  return (
    counts.validationErrorCount +
    counts.validationWarningCount +
    counts.validationInfoCount
  );
}

function formatSeverityPart(
  count: number,
  singular: string,
  plural: string
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Build a toast message that lists error / warning / info counts.
 * Example: "Table has 2 errors, 1 warning, 0 info."
 */
export function formatValidationResultToastMessage(
  subject: string,
  counts: ValidationSeverityCounts
): string {
  const total = totalValidationIssueCount(counts);
  if (total === 0) {
    return `${subject} has no validation issues.`;
  }

  const parts = [
    formatSeverityPart(counts.validationErrorCount, "error", "errors"),
    formatSeverityPart(counts.validationWarningCount, "warning", "warnings"),
    formatSeverityPart(counts.validationInfoCount, "info", "info"),
  ];

  return `${subject} has ${parts.join(", ")}.`;
}
